import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { announcementService } from '../../../services/announcementService';
import { useAuth } from '../../../contexts/AuthContext';

const gradientStyle = {
  background: 'linear-gradient(135deg, #1a56db 0%, #3b82f6 50%, #1d4ed8 100%)',
  border: 'none',
  color: '#ffffff',
};

const GradientButton = ({ children, onClick, iconName, size, className = '', disabled = false, type = 'button' }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    className={`inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-lg font-medium text-sm transition-all duration-200 hover:opacity-90 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg ${className}`}
    style={gradientStyle}
  >
    {iconName && <Icon name={iconName} size={size === 'sm' ? 14 : 16} color="#ffffff" />}
    {children}
  </button>
);

const AnnouncementManagementPanel = () => {
  const { user, isAdmin, getClientForRole } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);

  const emptyForm = {
    title: '', content: '',
    fileUrl: '', fileType: '',
    ctaButtonText: '', ctaButtonUrl: '',
    status: 'draft',
  };

  const [formData, setFormData] = useState(emptyForm);

  const statusOptions = [
    { value: 'draft',     label: 'Draft'     },
    { value: 'published', label: 'Published' },
    { value: 'archived',  label: 'Archived'  },
  ];

  const getClient = () => getClientForRole?.('admin') || getClientForRole?.('customer');

  useEffect(() => {
    if (isAdmin) loadAnnouncements();
  }, [isAdmin]);

  const loadAnnouncements = async () => {
    try {
      setLoading(true);
      const data = await announcementService.getAllAnnouncements(getClient());
      setAnnouncements(data);
    } catch (err) {
      setError(err?.message || 'Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    try {
      setUploadingFile(true);
      setError(null);
      const result = await announcementService.uploadAnnouncementFile(file, getClient());
      setFormData(prev => ({
        ...prev,
        fileUrl:  result?.url  || '',
        fileType: file?.type   || '',
      }));
    } catch (err) {
      setError(err?.message || 'Failed to upload file');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (!isAdmin) { setError('Only admins can create announcements'); return; }
    if (!formData.title?.trim()) { setError('Title is required'); return; }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const dataToSave = {
        ...formData,
        createdBy:   user?.id,
        publishedAt: formData.status === 'published' ? new Date().toISOString() : null,
      };

      if (editingId) {
        await announcementService.updateAnnouncement(editingId, dataToSave, getClient());
      } else {
        await announcementService.createAnnouncement(dataToSave, getClient());
      }

      setSuccess(true);
      setFormData(emptyForm);
      setIsCreating(false);
      setEditingId(null);
      await loadAnnouncements();
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err?.message || `Failed to ${editingId ? 'update' : 'create'} announcement`);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (announcement) => {
    setEditingId(announcement?.id);
    setFormData({
      title:         announcement?.title         || '',
      content:       announcement?.content       || '',
      fileUrl:       announcement?.fileUrl       || '',
      fileType:      announcement?.fileType      || '',
      ctaButtonText: announcement?.ctaButtonText || '',
      ctaButtonUrl:  announcement?.ctaButtonUrl  || '',
      status:        announcement?.status        || 'draft',
    });
    setIsCreating(true);
    setError(null);
  };

  const handleCancelEdit = () => {
    setIsCreating(false);
    setEditingId(null);
    setFormData(emptyForm);
    setError(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) return;
    try {
      setLoading(true);
      await announcementService.deleteAnnouncement(id, getClient());
      await loadAnnouncements();
    } catch (err) {
      setError(err?.message || 'Failed to delete announcement');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      setLoading(true);
      await announcementService.updateAnnouncement(id, {
        status:      newStatus,
        publishedAt: newStatus === 'published' ? new Date().toISOString() : null,
      }, getClient());
      await loadAnnouncements();
    } catch (err) {
      setError(err?.message || 'Failed to update status');
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin) return null;

  return (
    <div className="bg-card rounded-lg shadow-md border border-border">
      <div className="p-4 md:p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
              <Icon name="Megaphone" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-lg md:text-xl font-semibold text-foreground">Announcement Management</h2>
              <p className="text-sm text-muted-foreground">Create and manage customer announcements</p>
            </div>
          </div>
          {!isCreating && (
            <GradientButton iconName="Plus" size="sm" onClick={() => setIsCreating(true)}>
              New Announcement
            </GradientButton>
          )}
        </div>
      </div>

      <div className="p-4 md:p-6">
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <Icon name="AlertCircle" size={18} color="#dc2626" />
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}
        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2">
            <Icon name="CheckCircle2" size={18} color="#16a34a" />
            <p className="text-sm text-green-600">Announcement saved successfully!</p>
          </div>
        )}

        {/* ── FORM ── */}
        {isCreating && (
          <form onSubmit={handleSubmit} className="mb-6 p-4 bg-muted/30 rounded-lg border border-border">
            <h3 className="text-base font-semibold text-foreground mb-4">
              {editingId ? 'Edit Announcement' : 'Create New Announcement'}
            </h3>
            <div className="space-y-4">

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Title *</label>
                <Input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Enter announcement title"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Content</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                  placeholder="Enter announcement content"
                  rows={3}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Upload File (Image/PDF)</label>
                <div className="flex items-center gap-3 flex-wrap">
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="announcement-file-upload"
                  />
                  <label
                    htmlFor="announcement-file-upload"
                    className="px-4 py-2 bg-background border border-border rounded-lg cursor-pointer hover:bg-muted transition-colors flex items-center gap-2"
                  >
                    <Icon name="Upload" size={16} />
                    <span className="text-sm">{uploadingFile ? 'Uploading...' : 'Choose File'}</span>
                  </label>
                  {formData.fileUrl && (
                    <span className="text-sm text-green-600 flex items-center gap-1">
                      <Icon name="CheckCircle2" size={16} color="#16a34a" />
                      File ready
                    </span>
                  )}
                </div>
                {formData.fileUrl && formData.fileType?.startsWith('image/') && (
                  <img
                    src={formData.fileUrl}
                    alt="Preview"
                    className="mt-2 rounded-lg max-h-32 object-contain border border-border"
                  />
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">CTA Button Text</label>
                  <Input
                    type="text"
                    value={formData.ctaButtonText}
                    onChange={(e) => setFormData(prev => ({ ...prev, ctaButtonText: e.target.value }))}
                    placeholder="e.g., Upgrade Now"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">CTA Button URL</label>
                  <Input
                    type="text"
                    value={formData.ctaButtonUrl}
                    onChange={(e) => setFormData(prev => ({ ...prev, ctaButtonUrl: e.target.value }))}
                    placeholder="e.g., /settings"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full h-10 px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary cursor-pointer"
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="archived">Archived</option>
                </select>
              </div>

              <div className="flex gap-3 pt-2">
                <GradientButton
                  type="submit"
                  disabled={loading || uploadingFile}
                  className="px-4 py-2 h-10"
                >
                  {loading
                    ? (editingId ? 'Updating...' : 'Creating...')
                    : (editingId ? 'Update Announcement' : 'Create Announcement')}
                </GradientButton>
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="inline-flex items-center justify-center h-10 px-4 py-2 rounded-md text-sm font-medium border border-input hover:bg-accent hover:text-accent-foreground transition-colors"
                >
                  Cancel
                </button>
              </div>

            </div>
          </form>
        )}

        {/* ── LIST ── */}
        <div className="space-y-3">
          {loading && announcements.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">Loading announcements...</div>
          ) : announcements.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Icon name="Megaphone" size={48} className="mx-auto mb-3 opacity-30" />
              <p>No announcements yet. Create your first one!</p>
            </div>
          ) : (
            announcements.map((announcement) => (
              <div key={announcement.id} className="p-4 bg-background border border-border rounded-lg">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold text-foreground">{announcement.title}</h3>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                        announcement.status === 'published'
                          ? 'bg-green-100 text-green-700 border border-green-200'
                          : announcement.status === 'archived'
                          ? 'bg-gray-100 text-gray-700 border border-gray-200'
                          : 'bg-yellow-100 text-yellow-700 border border-yellow-200'
                      }`}>
                        {announcement.status === 'published' ? '✓ Published'
                          : announcement.status === 'archived' ? 'Archived' : 'Draft'}
                      </span>
                    </div>
                    {announcement.content && (
                      <p className="text-sm text-muted-foreground mb-2">{announcement.content}</p>
                    )}
                    {announcement.fileUrl ? (
                      <div className="flex items-center gap-2 text-sm text-primary mb-2">
                        <Icon name="Paperclip" size={14} />
                        <span>File attached</span>
                        {announcement.fileType?.startsWith('image/') && (
                          <img
                            src={announcement.fileUrl}
                            alt="thumb"
                            className="ml-2 h-8 w-auto rounded object-contain border border-border"
                          />
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400 mb-2">No file attached</p>
                    )}
                    {announcement.ctaButtonText && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Icon name="MousePointerClick" size={14} />
                        <span>CTA: {announcement.ctaButtonText}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Select
                      value={announcement.status}
                      onChange={(newStatus) => handleStatusChange(announcement.id, newStatus)}
                      options={statusOptions}
                      className="text-sm"
                    />
                    <GradientButton size="sm" onClick={() => handleEdit(announcement)}>
                      <Icon name="Edit" size={14} color="#ffffff" />
                    </GradientButton>
                    <GradientButton size="sm" onClick={() => handleDelete(announcement.id)}>
                      <Icon name="Trash2" size={14} color="#ffffff" />
                    </GradientButton>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AnnouncementManagementPanel;